import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import CurrencyHelper from 'presentation/formatters/currency/index.js';

const BetslipUtils = MojitoServices.Betslip.utils;
const { NumberUtils } = MojitoCore.Base;
/**
 * Utility functions for bonus indicators.
 *
 * @class BonusIndicatorUtils
 * @memberof Mojito.Modules
 */
export default class BonusIndicatorUtils {
    static getAccaBoostTitle(bonusType, bonusInfo, stringResolver) {
        const { rate } = bonusInfo;
        const bonusRate = Number(rate);
        const hasRate = !isNaN(bonusRate) && bonusRate !== 1;
        const percent = hasRate
            ? NumberUtils.percentageMultiplierToPercentageValue(bonusRate)
            : undefined;
        return BonusIndicatorUtils.getBoostTitle(
            bonusType,
            { ...bonusInfo, value: percent || bonusInfo.value },
            stringResolver
        );
    }

    static getBoostTitle(bonusType, bonusInfo, stringResolver) {
        const { contributionSource, oddsBoostSubtype, value } = bonusInfo;

        return value
            ? stringResolver.resolveAndFormatString(
                  BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
                      PROFIT: `$BONUS_INDICATOR.${bonusType}.BOOST_PROFIT_TITLE_WITH_PERCENT`,
                      RETURN: `$BONUS_INDICATOR.${bonusType}.BOOST_RETURNS_TITLE_WITH_PERCENT`,
                      FIXED: `$BONUS_INDICATOR.${bonusType}.BOOST_FIXED_TITLE_WITH_PRICE`,
                  }),
                  value
              )
            : stringResolver.resolveString(`$BONUS_INDICATOR.${bonusType}.TITLE`);
    }

    static getEnhancedEwTermsTitle(bonusInfo, stringResolver) {
        const { extraPlaces } = bonusInfo || {};

        if (!extraPlaces || isNaN(extraPlaces) || extraPlaces < 1) {
            return stringResolver.resolveString(`$BONUS_INDICATOR.ENHANCED_EW_TERMS.TITLE`);
        }

        const label =
            extraPlaces > 1
                ? stringResolver.resolveString(
                      '$BONUS_INDICATOR.ENHANCED_EW_TERMS.EXTRA_PLACES_PLURAL'
                  )
                : stringResolver.resolveString('$BONUS_INDICATOR.ENHANCED_EW_TERMS.EXTRA_PLACES');

        return stringResolver.formatString(label, extraPlaces);
    }

    static getAccaBoostMessage(bonus, stringResolver, currencyCode) {
        const { rate, maxCap, value } = bonus;
        const bonusRate = Number(rate);
        const bonusMaxCap = Number(maxCap) || 0;
        const bonusValue = Number(value) || 0;
        if (isNaN(bonusRate) || bonusRate === 1) {
            return;
        }
        const percent = NumberUtils.percentageMultiplierToPercentageValue(bonusRate);

        if (bonusValue) {
            return stringResolver.resolveAndFormatString(
                '$BONUS_INDICATOR.ACCA_BOOST.TEXT_WITH_VALUE',
                percent,
                CurrencyHelper.formatCurrency(bonusValue, currencyCode)
            );
        }

        return bonusMaxCap
            ? stringResolver.resolveAndFormatString(
                  '$BONUS_INDICATOR.ACCA_BOOST.TEXT_WITH_CAP',
                  percent,
                  CurrencyHelper.formatCurrency(bonusMaxCap, currencyCode)
              )
            : stringResolver.resolveAndFormatString(
                  '$BONUS_INDICATOR.ACCA_BOOST.TEXT_WITH_NO_CAP',
                  percent
              );
    }

    static getEtbMessage(bonus, stringResolver) {
        const { name, description } = bonus;
        return stringResolver.resolveAndFormatString(
            '$BONUS_INDICATOR.EVENT_TRIGGERED.TEXT',
            name,
            description
        );
    }

    static getEarlyPayoutMessage(bonus) {
        const { rules } = bonus;
        return rules
            ?.map(rule => rule?.text)
            .filter(Boolean)
            .join('\n');
    }
}
